import { useCallback, useState } from 'react';
import { ndrService, GetNdrParams, UpdateNdrPayload } from '../services/ndr';

export const useNdr = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getNdrList = useCallback(async (params: GetNdrParams) => {
    setLoading(true);
    setError(null);
    try {
      return await ndrService.getNdrByUserId(params);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch NDR data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateNdr = useCallback(
    async (userId: number, payload: UpdateNdrPayload) => {
      setLoading(true);
      setError(null);
      try {
        return await ndrService.updateNdr(userId, payload);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to update NDR');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getShipperRemarks = useCallback(async () => {
    try {
      return await ndrService.getShipperRemarks();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch shipper remarks');
      throw err;
    }
  }, []);

  const getConsigneeRemarks = useCallback(async () => {
    try {
      return await ndrService.getConsigneeRemarks();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch consignee remarks');
      throw err;
    }
  }, []);

  const getNdrHistory = useCallback(async (trackingNo: string) => {
    setLoading(true);
    setError(null);
    try {
      return await ndrService.getNdrHistory(trackingNo);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch NDR history');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadNdr = useCallback(async (courierId: string, fromDate?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ndrService.downloadNdr(courierId, fromDate);
      if (res?.success && res?.file) {
        const link = document.createElement('a');
        link.href = `data:text/csv;base64,${res.file}`;
        link.download = `NDR_Report_${courierId}${fromDate ? `_${fromDate}` : ''}.csv`;
        link.click();
        return res;
      } else {
        throw new Error(res?.message || 'Failed to download file');
      }
    } catch (err: any) {
      const errMessage = err?.response?.data?.message || err?.message || 'Failed to download report';
      setError(errMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,

    // actions
    getNdrList,
    updateNdr,
    getShipperRemarks,
    getConsigneeRemarks,
    getNdrHistory,
    downloadNdr,
  };
};
